import { type MouseEvent } from 'react';

import {
  CONTENT_TYPES,
  type ContentListItemType,
  type ItemTypeMap,
} from '@hivebrite/design-system/ContentList';
import { type IDropdownItem } from '@hivebrite/design-system/Dropdown';

import { makeConfigUrls as makeForumUrls } from 'hb-react/frontoffice/apps/Forum/utils/configUrls';
import { getEventDedicatedPath } from 'hb-react/shared/apps/Events/utils/getEventDedicatedUrl';
import getDateStr from 'hb-react/shared/components/EventDate/utils/getDateStr';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';
import decodeString from 'hb-react/shared/utils/decodeString';
import {
  ENTITY_TYPES,
  LEGACY_ENTITY_TYPES,
} from 'hb-react/shared/utils/entitiesContentData/constants';
import {
  type AdminEvent,
  type BusinessOpportunity,
  type Company,
  type EntityItem,
  type GroupEmbed,
  type File,
  type Folder,
  type ForumDiscussion,
  type News,
  type Post,
  type Project,
  type UserEvent,
  type EntityType,
} from 'hb-react/shared/utils/entitiesContentData/types';
import { type TimeZoneConfiguration } from 'hb-react/types/timeZoneConfiguration';

// Not exported from DS, but the same for all content types with a group
type ContentListGroupEmbed = ItemTypeMap[CONTENT_TYPES.EVENT]['group'];

export interface TransformerOptions {
  displayFields?: {
    [K in CONTENT_TYPES]?: ItemTypeMap[K]['displayFields'];
  };
  entityNames?: Partial<Record<EntityType, string>>;
  flags?: Record<string, boolean>;
  getItemActions?: (item: EntityItem) => Array<IDropdownItem>;
  isPublicOffice?: boolean;
  isWhiteLabelGroup?: boolean;
  timeZoneConfiguration?: TimeZoneConfiguration;
  withLink?: boolean;
}

// Ensures that the content id is unique and can be used as a key of React elements.
const getContentId = (item: EntityItem) => `${item.type}:${item.id}`;

const transformGroupData = (
  groupCreator?: GroupEmbed,
): ContentListGroupEmbed | undefined =>
  groupCreator && {
    id: groupCreator.id,
    name: groupCreator.name,
    logoSrc: groupCreator.logo?.thumbnailUrl,
  };

// Helper to transform common event data
const transformCommonEventData = (
  item: AdminEvent | UserEvent,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.EVENT] => {
  const {
    displayFields,
    getItemActions,
    isPublicOffice,
    isWhiteLabelGroup,
    timeZoneConfiguration = {},
    withLink,
  } = options;
  const { currentUserTimezone, eventDatesInUserTimezone } =
    timeZoneConfiguration;
  const timezone = eventDatesInUserTimezone
    ? currentUserTimezone
    : item.timezone;

  const date = getDateStr({
    startDate: item.startDate,
    endDate: undefined,
    timezone,
  });

  return {
    actions: getItemActions?.(item),
    contentType: CONTENT_TYPES.EVENT,
    contentTypeName: options.entityNames?.[item.type],
    date,
    displayFields: displayFields?.[CONTENT_TYPES.EVENT],
    group: transformGroupData(item.groupCreator),
    id: getContentId(item),
    location: item?.location?.location,
    formattedDate: {
      day: dayjs(item.startDate).tz(timezone).format('DD'),
      month: dayjs(item.startDate).tz(timezone).format('MMM'),
    },
    name: item.title,
    attendees: [],
    totalAttendees: 0,
    path: withLink
      ? getEventDedicatedPath({
          hasUser: !isPublicOffice,
          event: {
            type: (
              [
                LEGACY_ENTITY_TYPES.adminEvent,
                ENTITY_TYPES.adminEvent,
              ] as Array<EntityType>
            ).includes(item.type)
              ? 'network_events_event'
              : item.type,
            id: item.id,
          },
          topicId: item.groupCreator?.id,
          isWhitelabelGroup: isWhiteLabelGroup,
        })
      : undefined,
  };
};

const transformUserEvent = (
  item: UserEvent,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.EVENT] => ({
  image: item.logo?.mediumUrl,
  ...transformCommonEventData(item, options),
});

const transformAdminEvent = (
  item: AdminEvent,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.EVENT] => ({
  onlineEvent: Boolean(item.webinarUrl || item.isInternalZoomMeeting),
  image: item.coverPicture?.mediumUrl,
  ...transformCommonEventData(item, options),
});

// Individual transformers
const transformNews = (
  item: News,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.NEWS] => {
  const {
    displayFields,
    flags = {},
    getItemActions,
    isWhiteLabelGroup,
    withLink,
  } = options;
  const { flagDsIsolationNewsDetailPage } = flags;
  let navigationalProps: { onClick?: () => void; path?: string } = {};

  if (withLink) {
    const path =
      !isWhiteLabelGroup && item.groupCreator
        ? Routes.topic_new_path(item.groupCreator.id, item.id)
        : Routes.new_path(item.id);

    if (flagDsIsolationNewsDetailPage) {
      navigationalProps = {
        path,
        // @ts-expect-error onClick type will be updated within the next package release
        onClick: (event: MouseEvent) => {
          event.preventDefault();
          window.location.href = path;
        },
      };
    } else {
      navigationalProps = { path };
    }
  }

  return {
    actions: getItemActions?.(item),
    contentType: CONTENT_TYPES.NEWS,
    contentTypeName: options.entityNames?.[item.type],
    content: decodeString(item.sanitizedContent),
    displayFields: displayFields?.[CONTENT_TYPES.NEWS],
    id: getContentId(item),
    image: item.coverPicture?.mediumUrl,
    group: transformGroupData(item.groupCreator),
    title: item.subject,
    ...navigationalProps,
  };
};

const transformForumDiscussion = (
  item: ForumDiscussion,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.FORUM] => {
  const { displayFields, getItemActions, withLink } = options;
  const path = withLink
    ? makeForumUrls(item.groupCreator?.id).discussionUrl(item.id)
    : undefined;

  return {
    actions: getItemActions?.(item),
    author: {
      id: item.user.id,
      name: item.user.name,
      photoThumbUrl: item.user.photo.thumbnailUrl,
    },
    content: decodeString(item.sanitizedContent),
    contentType: CONTENT_TYPES.FORUM,
    contentTypeName: options.entityNames?.[item.type],
    displayFields: displayFields?.[CONTENT_TYPES.FORUM],
    id: getContentId(item),
    imageSrc: item.coverPicture?.mediumUrl,
    group: transformGroupData(item.groupCreator),
    name: item.title,
    path,
  };
};

const transformProject = (
  item: Project,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.PROJECT] => {
  const { displayFields, getItemActions, withLink } = options;

  let path: string | undefined;
  if (withLink) {
    path = item.groupCreator?.id
      ? Routes.topic_venture_path(item.groupCreator.id, item.id)
      : Routes.venture_path(item.id);
  }

  return {
    actions: getItemActions?.(item),
    contentType: CONTENT_TYPES.PROJECT,
    contentTypeName: options.entityNames?.[item.type],
    description: item.highLevelPitch,
    displayFields: displayFields?.[CONTENT_TYPES.PROJECT],
    image: item.logo?.mediumUrl,
    group: transformGroupData(item.groupCreator),
    id: getContentId(item),
    location: item.location?.location,
    name: item.companyName,
    path,
  };
};

const transformFolder = (
  item: Folder,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.FOLDER] => {
  const { displayFields, getItemActions, withLink } = options;

  let path: string | undefined;

  if (withLink) {
    if (item.type === ENTITY_TYPES.mediaCenterFolder) {
      path = item.groupCreator
        ? Routes.topic_media_center_folder_view_path(
            item.groupCreator.id,
            item.uuid,
          )
        : Routes.media_center_folder_view_path(item.uuid);
    }
    if (item.type === ENTITY_TYPES.portfolioFolder) {
      path = item.user
        ? Routes.user_portfolio_folder_view_path(item.user.id, item.uuid)
        : Routes.portfolios_path();
    }
  }

  let contentTypeName = options.entityNames?.[item.type];
  // Ensure that the content type name is set to the default name if it is not found.
  contentTypeName ??=
    item.type === ENTITY_TYPES.portfolioFolder
      ? __('Portfolio')
      : __('Media Center');

  return {
    actions: getItemActions?.(item),
    contentType: CONTENT_TYPES.FOLDER,
    contentTypeName,
    displayFields: displayFields?.[CONTENT_TYPES.FOLDER],
    id: getContentId(item),
    image: item.coverPicture?.mediumUrl ?? '',
    folderLabel: __('Folder'),
    group: transformGroupData(item.groupCreator),
    name: item.name,
    path,
  };
};

const transformFile = (
  item: File,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.FILE] => {
  const { displayFields, getItemActions, withLink } = options;

  let path: string | undefined = undefined;

  if (withLink) {
    if (item.type === ENTITY_TYPES.portfolioFile) {
      path = item.user
        ? Routes.user_portfolio_file_view_path(item.user.id, item.uuid)
        : Routes.portfolios_file_view_path(item.uuid);
    } else {
      path = item.groupCreator
        ? Routes.topic_media_center_file_view_path(
            item.groupCreator.id,
            item.uuid,
          )
        : Routes.media_center_file_view_path(item.uuid);
    }
  }

  let contentTypeName = options.entityNames?.[item.type];
  // Ensure that the content type name is set to the default name if it is not found.
  contentTypeName ??=
    item.type === ENTITY_TYPES.portfolioFile
      ? __('Portfolio')
      : __('Media Center');

  return {
    actions: getItemActions?.(item),
    contentType: CONTENT_TYPES.FILE,
    contentTypeName: contentTypeName,
    description: item.description,
    displayFields: displayFields?.[CONTENT_TYPES.FILE],
    extension: item.extension || '',
    fileType: item.fileType || 'application',
    group: transformGroupData(item.groupCreator),
    id: getContentId(item),
    image: item.thumbnail?.previewUrl || item.attachment?.previewUrl,
    name: item.name,
    path,
    user: item.user
      ? {
          id: item.user.id,
          name: item.user.name,
          path: Routes.user_path(item.user.id),
          photo_thumb_url: item.user.photo.thumbnailUrl,
        }
      : undefined,
  };
};

const transformCompany = (
  item: Company,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.ORGANIZATION] => {
  const { displayFields, getItemActions, withLink } = options;

  const path = withLink ? Routes.company_path(item.id) : undefined;

  return {
    actions: getItemActions?.(item),
    contentType: CONTENT_TYPES.ORGANIZATION,
    contentTypeName: options.entityNames?.[item.type],
    description: item.shortDescription,
    displayFields: displayFields?.[CONTENT_TYPES.ORGANIZATION],
    id: getContentId(item),
    image: item.logo?.mediumUrl,
    name: item.name,
    path,
  };
};

const transformBusinessOpportunity = (
  item: BusinessOpportunity,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.OPPORTUNITY] => {
  const { displayFields, getItemActions, withLink } = options;

  let path: string | undefined;
  if (withLink) {
    path = item.groupCreator
      ? Routes.topic_jobs_show_path(item.groupCreator.id, item.id)
      : Routes.opportunity_path(item.id);
  }

  return {
    actions: getItemActions?.(item),
    contentType: CONTENT_TYPES.OPPORTUNITY,
    contentTypeName: options.entityNames?.[item.type],
    companyName: decodeString(item.companyName),
    displayFields: displayFields?.[CONTENT_TYPES.OPPORTUNITY],
    id: getContentId(item),
    image: item.logo?.mediumUrl,
    group: transformGroupData(item.groupCreator),
    location: item.location?.location,
    name: decodeString(item.title || item.companyName),
    path,
  };
};

const transformPost = (
  item: Post,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.POST] => {
  const MAX_CONTENT_LENGTH = 500;
  const { displayFields, getItemActions, withLink } = options;

  let path: string | undefined;
  if (withLink) {
    path = item.groupCreator
      ? Routes.topic_topic_feed_posts_path(item.groupCreator.id, item.id)
      : Routes.feed_post_path(item.id);
  }

  return {
    actions: getItemActions?.(item),
    author: {
      id: item.user.id,
      name: item.user.name,
      photoThumbUrl: item.user.photo.thumbnailUrl,
    },
    content: decodeString(item.contentV2).slice(0, MAX_CONTENT_LENGTH),
    contentType: CONTENT_TYPES.POST,
    contentTypeName: options.entityNames?.[item.type],
    displayFields: displayFields?.[CONTENT_TYPES.POST],
    id: getContentId(item),
    group: transformGroupData(item.groupCreator),
    path,
  };
};

const defaultOptions: TransformerOptions = {
  isPublicOffice: true,
  isWhiteLabelGroup: false,
  flags: {},
  withLink: true,
};

// Add function overloads before the actual implementation
export function transformItem(
  item: News,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.NEWS];

export function transformItem(
  item: ForumDiscussion,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.FORUM];

export function transformItem(
  item: AdminEvent | UserEvent,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.EVENT];

export function transformItem(
  item: Project,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.PROJECT];

export function transformItem(
  item: Company,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.ORGANIZATION];

export function transformItem(
  item: Folder,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.FOLDER];

export function transformItem(
  item: File,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.FILE];

export function transformItem(
  item: BusinessOpportunity,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.OPPORTUNITY];

export function transformItem(
  item: Post,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.POST];

export function transformItem(
  item: EntityItem,
  options: TransformerOptions,
): ContentListItemType | undefined;

export function transformItem(item: EntityItem, options: TransformerOptions) {
  const opts = { ...defaultOptions, ...options };

  switch (item.type) {
    case ENTITY_TYPES.news:
      return transformNews(item, opts);
    case ENTITY_TYPES.forumDiscussion:
      return transformForumDiscussion(item, opts);
    case ENTITY_TYPES.userEvent:
      return transformUserEvent(item, opts);
    case ENTITY_TYPES.adminEvent:
    case LEGACY_ENTITY_TYPES.adminEvent:
      return transformAdminEvent(item, opts);
    case ENTITY_TYPES.project:
      return transformProject(item, opts);
    case ENTITY_TYPES.company:
      return transformCompany(item, opts);
    case ENTITY_TYPES.mediaCenterFolder:
    case ENTITY_TYPES.portfolioFolder:
      return transformFolder(item, opts);
    case ENTITY_TYPES.mediaCenterFile:
    case ENTITY_TYPES.portfolioFile:
      return transformFile(item, opts);
    case ENTITY_TYPES.opportunity:
    case LEGACY_ENTITY_TYPES.opportunity:
      return transformBusinessOpportunity(item, opts);
    case ENTITY_TYPES.post:
    case LEGACY_ENTITY_TYPES.post:
      return transformPost(item, opts);
    default:
      // @ts-expect-error typescript consider `item` as `never` since all known types are handled above
      // eslint-disable-next-line no-console
      console.error(`Transformer not found for item type: ${item.type}`);
  }

  return undefined;
}

export const transformDataList = (
  items: Array<EntityItem>,
  options: TransformerOptions = {},
): Array<ContentListItemType> =>
  items.map((item) => transformItem(item, options)).filter(Boolean);
